/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileSize, MaxFileSizeError, parseFileSize } from '@umss/shared/utils';

@Component({
  selector: 'umss-input-file',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './input-file.component.html',
  styleUrls: ['./input-file.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssInputFileComponent),
      multi: true
    }
  ]
})
export class UmssInputFileComponent implements ControlValueAccessor, OnChanges {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  @Input() label?: string;
  @Input() buttonText: string = 'Choose File';
  @Input() noFileText: string = 'No file chosen';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() accept?: string; // File types to accept (e.g., ".pdf,.doc,.docx")
  @Input() multiple: boolean = false; // Allow multiple file selection
  @Input() maxSize?: FileSize; // Max file size in bytes. It blocks files larger than the specified size

  /** Variant props */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;

  /** File data */
  private _files: File[] = [];
  selectedFileName: string = '';
  readonly inputId = `umss-input-file-${Math.random().toString(36).slice(2)}`;
  /**
   * Max file size in bytes. If undefined, there is no limit
   */
  private maxBytes?: number;

  /** Event outputs */
  @Output() fileChange = new EventEmitter<File[]>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() maxFileSizeError = new EventEmitter<MaxFileSizeError>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: File[] | null) => void = () => {};
  private _onTouched: () => void = () => {};

  @ViewChild('fileInput') inputFileEl!: ElementRef;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxSize']) {
      if (!this.maxSize) return;
      this.maxBytes = parseFileSize(this.maxSize).bytes;
    }
  }
  writeValue(value: File[] | null): void {
    this._files = value || [];
    this.updateFileName();
  }

  registerOnChange(fn: (value: File[] | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (files && files.length > 0) {
      const [allowed, notAllowed] = this.splitFiles(Array.from(files));

      const parsed = parseFileSize(this.maxSize);

      this.maxFileSizeError.emit({
        files: notAllowed,
        maxSizeBytes: parsed.bytes,
        maxSizeUnit: parsed.unit,
        maxSize: this.maxSize
      });

      if (allowed.length === 0) return;

      this._files = allowed;
      this.updateFileName();
      this._onChange(this._files);
      this.fileChange.emit(this._files);
    }
  }

  private splitFiles(files: File[]): [File[], File[]] {
    if (!this.maxBytes) return [files, []];
    return files.reduce(
      ([allowed, notAllowed], file) => {
        if (this.maxBytes && file.size > this.maxBytes) {
          return [allowed, [...notAllowed, file]];
        }
        return [[...allowed, file], notAllowed];
      },
      [[], []] as [File[], File[]]
    );
  }

  open(): void {
    this.inputFileEl.nativeElement.click();
  }

  onFileFocus(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onFileBlur(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  /** Helper methods */
  updateFileName(): void {
    if (this._files.length === 0) {
      this.selectedFileName = '';
    } else if (this._files.length === 1) {
      this.selectedFileName = this._files[0].name;
    } else {
      this.selectedFileName = `${this._files.length} files selected`;
    }
  }

  /** Computed properties */
  get hasFile(): boolean {
    return this._files.length > 0;
  }

  get displayText(): string {
    return this.hasFile ? this.selectedFileName : this.noFileText;
  }
}
